/// <reference types="vite/client" />

// GET /api/v2/execution-playbooks/:clientId/:useCaseId/approval-artifacts?artifact_id=...

import { ExecutionPlaybooksError } from './executionPlaybooksService';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'http://localhost:8080';

export type ApprovalArtifactId =
  | 'executive_one_pager'
  | 'cfo_roi_summary'
  | 'board_rationale'
  | 'assumptions_appendix';

export interface ApprovalArtifactScenarioRow {
  scenario: string;
  roi_range_display: string;
  payback_display: string;
}

export interface ApprovalArtifactPhase {
  phase?: number;
  label?: string;
  title?: string;
  description?: string;
  duration?: string;
}

export interface ApprovalArtifactHeaderCard {
  title: string;
  initiative_name?: string;
  summary_paragraph: string;
  organization_name?: string;
  status_badge: string;
  version_label: string;
  generated_date: string;
  confidence_score_pct?: number;
  confidence_display?: string;
}

export interface ApprovalArtifactProjectedScenario {
  scenario: string;
  roi_low_display?: string;
  roi_high_display?: string;
  roi_range_display?: string;
  payback_display?: string;
  payback_months?: number;
  annual_benefit?: number;
  annual_yield_display?: string;
}

export interface ApprovalArtifactPaybackRow {
  scenario: string;
  roi_range_display: string;
  payback_display: string;
  annual_yield_display?: string;
}

export interface ApprovalArtifactFinancialAssumption {
  id: string;
  label: string;
  value?: number;
  value_display?: string;
  unit?: string | null;
  source?: string;
  source_type?: string | null;
  confidence_pct?: number;
  category?: string;
  driver_type?: string;
  role?: string;
}

export interface ApprovalArtifactRiskFactor {
  risk_type?: string;
  title?: string;
  risk: string;
  mitigation: string;
  severity?: string;
  likelihood?: string;
}

export interface ApprovalArtifactPayload {
  artifact_id: ApprovalArtifactId | string;
  title: string;
  subtitle: string;
  document_status: string;
  version: number;
  generated_at: string;
  client_id: string;
  use_case_id: number;
  header_card: ApprovalArtifactHeaderCard;
  financial_impact_summary?: {
    subtitle?: string;
    roi_range_base_display?: string;
    payback_period_display?: string;
    confidence_score_pct?: number;
    confidence_display?: string;
    implementation_effort?: string;
    primary_value_driver?: string;
  };
  projected_roi?: {
    subtitle?: string;
    scenarios?: ApprovalArtifactProjectedScenario[];
  };
  payback_period?: {
    subtitle?: string;
    rows?: ApprovalArtifactPaybackRow[];
  };
  scenario_analysis?: {
    subtitle?: string;
    narrative?: string;
    primary_scenario_risk?: string;
  };
  key_financial_assumptions?: {
    subtitle?: string;
    assumptions?: ApprovalArtifactFinancialAssumption[];
    fallback_text?: string | null;
    assumptions_appendix_href?: string;
    roi_model_path?: string;
  };
  cfo_recommendation?: {
    subtitle?: string;
    text?: string;
  };
  strategic_alignment?: {
    subtitle?: string;
    narrative?: string;
    tags?: {
      mission?: string;
      organizational_priority?: string;
      strategic_goal?: string;
    };
  };
  problem_statement?: {
    subtitle?: string;
    value_driver?: string;
    cost_of_inaction?: string;
  };
  organizational_impact?: {
    subtitle?: string;
    narrative?: string;
    phase_count?: number;
    expected_outcomes?: string[];
  };
  financial_benefits?: {
    subtitle?: string;
    annual_revenue_display?: string;
    payback_period_display?: string;
    cum_npv_5y_display?: string;
    cum_roi_5y_pct?: number;
    cum_roi_5y_display?: string;
    confidence_score_pct?: number;
    confidence_display?: string;
    three_year_value_display?: string;
    scenario_summary?: {
      scenario: string;
      annual_roi_display: string;
      payback_display: string;
    }[];
  };
  operational_benefits?: {
    subtitle?: string;
    bullets?: string[];
  };
  execution_feasibility?: {
    subtitle?: string;
    narrative?: string;
    primary_adoption_risk?: string;
    primary_risk_detail?: {
      name: string;
      severity: string;
      likelihood: string;
    };
    mitigation?: string;
  };
  financial_assumptions?: {
    subtitle?: string;
    assumptions?: ApprovalArtifactFinancialAssumption[];
    fallback_text?: string | null;
  };
  operational_assumptions?: {
    subtitle?: string;
    assumptions?: ApprovalArtifactFinancialAssumption[];
    fallback_text?: string | null;
  };
  data_requirements?: {
    subtitle?: string;
    assumptions?: ApprovalArtifactFinancialAssumption[];
    fallback_text?: string | null;
  };
  dependencies?: {
    subtitle?: string;
    items?: { label?: string; description?: string; severity?: string }[];
    fallback_text?: string | null;
  };
  risk_factors?: {
    subtitle?: string;
    primary?: ApprovalArtifactRiskFactor;
    adoption?: ApprovalArtifactRiskFactor;
    data_quality?: ApprovalArtifactRiskFactor;
  };
  scenario_inputs?: {
    subtitle?: string;
    table?: {
      parameters?: {
        parameter: string;
        conservative: string;
        base: string;
        aggressive: string;
      }[];
      columns?: string[];
      note?: string;
    };
  };
  initiative_overview?: {
    subtitle?: string;
    category?: string;
    implementation_effort?: string;
    time_to_value?: string;
    confidence_score_pct?: number;
    confidence_display?: string;
    rationale?: string;
  };
  strategic_objective?: {
    subtitle?: string;
    primary_value_driver?: string;
    expected_outcomes?: string[];
  };
  financial_impact?: {
    subtitle?: string;
    roi_range_base_display?: string;
    payback_period_display?: string;
    confidence_score_pct?: number;
    confidence_display?: string;
    scenario_analysis?: ApprovalArtifactScenarioRow[];
  };
  execution_plan?: {
    subtitle?: string;
    time_to_value?: string;
    phases?: ApprovalArtifactPhase[];
  };
  risks_mitigations?: {
    subtitle?: string;
    primary_risk?: string;
    primary_risk_detail?: {
      name: string;
      severity: string;
      likelihood: string;
    };
    mitigation?: string;
    dependency_risks?: { name?: string; description?: string }[];
  };
  recommendation?: {
    subtitle?: string;
    text?: string;
    board_rationale_summary?: string;
  };
  sources?: Record<string, string>;
}

export interface ApprovalArtifactResponse {
  success: boolean;
  artifact: ApprovalArtifactPayload;
}

export async function fetchApprovalArtifact(
  clientId: string,
  useCaseId: string | number,
  artifactId: ApprovalArtifactId | string,
): Promise<ApprovalArtifactResponse> {
  if (!clientId || useCaseId == null) {
    throw new ExecutionPlaybooksError('Client ID and use case ID are required.');
  }

  const params = new URLSearchParams({ artifact_id: artifactId });
  const url = `${API_BASE}/api/v2/execution-playbooks/${encodeURIComponent(clientId)}/${encodeURIComponent(String(useCaseId))}/approval-artifacts?${params}`;

  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include' });
  } catch {
    throw new ExecutionPlaybooksError(
      'Unable to reach the approval artifacts service. Check your network connection and ensure the backend is running.',
    );
  }

  if (!res.ok) {
    let detail = '';
    try {
      const errBody = await res.json();
      detail = errBody?.message || errBody?.error || '';
    } catch {
      /* ignore */
    }
    throw new ExecutionPlaybooksError(
      detail || `Approval artifacts API returned ${res.status}.`,
      res.status,
    );
  }

  let body: ApprovalArtifactResponse;
  try {
    body = await res.json();
  } catch {
    throw new ExecutionPlaybooksError('Invalid response from approval artifacts API.');
  }

  if (!body?.success || !body.artifact) {
    throw new ExecutionPlaybooksError('Approval artifacts API reported failure.');
  }

  return body;
}
